// @ts-strict
import { useMemo, useCallback, useRef, useEffect, memo, useState } from 'react';
import { QueryClient } from '@tanstack/react-query';

/**
 * Performance optimization utilities for React components
 */

// Memoized component wrapper with display name
export function createMemoizedComponent<P extends object>(
  Component: React.ComponentType<P>,
  displayName?: string
) {
  const Memoized = memo(Component);
  if (displayName) {
    Memoized.displayName = displayName;
  }
  return Memoized;
}

// Stable callback creator
export function useStableCallback<T extends (...args: any[]) => any>(callback: T): T {
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  return useCallback((...args: Parameters<T>) => {
    return callbackRef.current(...args);
  }, []) as T;
}

// Stable object creator
export function useStableValue<T extends Record<string, any>>(value: T): T {
  return useMemo(() => value, Object.values(value));
}

// Debounce hook for search and input handling
export function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

// Throttle hook for scroll and resize handlers
export function useThrottle<T extends (...args: any[]) => any>(
  callback: T,
  delay: number
): T {
  const lastRan = useRef(Date.now());

  return useCallback(((...args) => {
    if (Date.now() - lastRan.current >= delay) {
      callback(...args);
      lastRan.current = Date.now();
    }
  }) as T, [callback, delay]);
}

// Intersection Observer hook for lazy loading
export function useIntersectionObserver(
  elementRef: React.RefObject<Element>,
  options: IntersectionObserverInit = {}
) {
  const [isIntersecting, setIsIntersecting] = useState(false);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsIntersecting(entry.isIntersecting);
      },
      {
        threshold: 0.1,
        rootMargin: '50px',
        ...options,
      }
    );

    observer.observe(element);

    return () => {
      observer.unobserve(element);
    };
  }, [elementRef, options]);

  return isIntersecting;
}

// Virtual scrolling hook for large lists
export function useVirtualScroll<T>(
  items: T[],
  itemHeight: number,
  containerHeight: number,
  overscan = 5
) {
  const [scrollTop, setScrollTop] = useState(0);

  const startIndex = Math.max(0, Math.floor(scrollTop / itemHeight) - overscan);
  const endIndex = Math.min(
    items.length - 1,
    Math.ceil((scrollTop + containerHeight) / itemHeight) + overscan
  );

  const visibleItems = items.slice(startIndex, endIndex + 1);
  const offsetY = startIndex * itemHeight;

  return {
    visibleItems,
    startIndex,
    endIndex,
    offsetY,
    onScroll: (event: React.UIEvent<HTMLDivElement>) => {
      setScrollTop(event.currentTarget.scrollTop);
    },
  };
}

// Optimized QueryClient configuration
export function createOptimizedQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 2 * 60 * 1000, // 2 min – balance freshness vs refetch load
        gcTime: 15 * 60 * 1000, // 15 min – keep unused cache for back navigation
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        retry: (failureCount, error) => {
          // Don't retry on 4xx errors
          if (error instanceof Error && 'status' in error && typeof error.status === 'number') {
            if (error.status >= 400 && error.status < 500) {
              return false;
            }
          }
          return failureCount < 3;
        },
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      },
      mutations: {
        retry: false, // Don't retry mutations
      },
    },
  });
}

// Image lazy loading hook
export function useLazyImage(src: string, placeholder?: string) {
  const [imageSrc, setImageSrc] = useState(placeholder || '');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      setImageSrc(src);
      setIsLoading(false);
    };
    img.onerror = () => {
      setError('Failed to load image');
      setIsLoading(false);
    };
    img.src = src;
  }, [src]);

  return { imageSrc, isLoading, error, imgRef };
}

// Bundle splitting utilities
export const lazyComponents = {
  // Lazy load heavy components
  Dashboard: () => import('@/pages/app/DashboardPage'),
  Analytics: () => import('@/pages/app/AnalyticsPage'),
  Campaigns: () => import('@/pages/app/CampaignsPage'),
  Audiences: () => import('@/pages/app/AudiencesPage'),
  Billing: () => import('@/pages/app/BillingPage'),
  Settings: () => import('@/pages/app/settings/SettingsPage'),

  // Admin components
  ErrorMonitoring: () => import('@/pages/app/admin/ErrorMonitoringPage'),
  AutomatedBilling: () => import('@/pages/app/AutomatedBillingPage'),
};

// Preload critical components
export function preloadCriticalComponents() {
  // Preload dashboard and settings for authenticated users
  lazyComponents.Dashboard().catch(() => {});
  lazyComponents.Settings().catch(() => {});
}

// Resource hints for performance
export const performanceHints = {
  // Preconnect to external domains
  preconnect: [
    'https://fonts.googleapis.com',
    'https://fonts.gstatic.com',
    'https://js.stripe.com',
    'https://api.stripe.com',
  ],

  // DNS prefetch for common domains
  dnsPrefetch: [
    'https://cdn.jsdelivr.net',
    'https://unpkg.com',
  ],

  // Preload critical resources
  preload: [
    '/favicon.ico',
    '/manifest.json',
  ],
};

// Performance monitoring hook
export function usePerformanceMonitor(componentName: string) {
  const renderCount = useRef(0);
  const lastRenderTime = useRef(Date.now());

  useEffect(() => {
    renderCount.current += 1;
    const now = Date.now();
    const renderTime = now - lastRenderTime.current;

    // Log performance issues in development
    if (import.meta.env.DEV) {
      if (renderCount.current > 10) {
        console.warn(`${componentName} has rendered ${renderCount.current} times`);
      }
      if (renderTime > 16) { // More than one frame
        console.warn(`${componentName} took ${renderTime}ms to render`);
      }
    }

    lastRenderTime.current = now;
  });

  return {
    renderCount: renderCount.current,
    averageRenderTime: renderCount.current > 1
      ? (Date.now() - lastRenderTime.current) / renderCount.current
      : 0,
  };
}

// Memory usage monitoring
export function useMemoryMonitor() {
  const [memoryUsage, setMemoryUsage] = useState<any>(null);

  useEffect(() => {
    const updateMemoryUsage = () => {
      if ('memory' in performance) {
        setMemoryUsage((performance as any).memory);
      }
    };

    updateMemoryUsage();
    const interval = setInterval(updateMemoryUsage, 10000); // Update every 10 seconds

    return () => clearInterval(interval);
  }, []);

  return memoryUsage;
}

// Web Vitals tracking
export function useWebVitals() {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    import('web-vitals').then((mod: { getCLS: (f: (m: unknown) => void) => void; getFID: (f: (m: unknown) => void) => void; getFCP: (f: (m: unknown) => void) => void; getLCP: (f: (m: unknown) => void) => void; getTTFB: (f: (m: unknown) => void) => void }) => {
      mod.getCLS(console.log);
      mod.getFID(console.log);
      mod.getFCP(console.log);
      mod.getLCP(console.log);
      mod.getTTFB(console.log);
    }).catch(() => {});
  }, []);
}